import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';
import ModalShell from 'components/common/ModalShell';
import { SegmentedToggle } from 'components/common/SegmentedToggle';
import { CopyToClipboard } from 'components/common/ProjectIcons';
import { copyToClipboard } from 'components/common/copyToClipboard';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { MONO_FONT_FAMILY } from 'fonts';
import { MONACO_OPTIONS } from 'components/routes/schema-builder/editorConfig';
import { SelectMenu } from 'components/routes/schema-builder/SelectMenu';
import { registerJqLanguage } from 'components/routes/jqLanguage';
import {
  AdditionalRow,
  ConditionRow,
  OutputPreview,
  TreeNode,
  useSourcePick,
} from 'components/routes/JqFilterBuilder';
import {
  genJq,
  normalizeTransforms,
  parseJqToBuilderState,
} from 'components/routes/jq-filter-builder/helpers';
import {
  AdditionalField,
  Condition,
} from 'components/routes/jq-filter-builder/types';
import {
  HEADER_CLASSES,
  PANEL_CLASSES,
  SECTION_LABEL,
} from 'components/routes/jq-filter-builder/styles';
import {
  exampleFor,
  inputSchemaFor,
  JqTransformAnnotation,
  outputTypeLabel,
  samplesAction,
} from './annotation';
import { hashString, stableStringify } from 'api/referenceOptions';
import { CapturedSample } from 'api/dataSamples';
import { OutboundPreview, previewInput, previewOutbound } from './preview';
import { SamplesState, useDataSamples } from './useDataSamples';
import {
  EditorSession,
  EditorSessionStore,
  liveEditorSession,
} from './editorSessions';

const EXAMPLE_KEY = 'example';
const CODE_EDITOR_MIN_HEIGHT = 360;

// Rows carry a stable id so React keys survive removals (index keys would move a row's
// transform popover onto its neighbour).
type Row<T> = { id: number; item: T };
let nextRowId = 0;
const toRows = <T,>(items: T[]): Row<T>[] =>
  items.map((item) => ({ id: nextRowId++, item }));
const itemsOf = <T,>(rows: Row<T>[]): T[] => rows.map((r) => r.item);
const emptyFieldRows = (): Row<AdditionalField>[] =>
  toRows([{ name: '', path: '' }]);

/** Keeps a captured-sample selection on the same record when Refresh reorders the list. */
export const capturedSampleKey = (sample: CapturedSample) =>
  `captured:${sample.captured_at}:${hashString(stableStringify(sample.record))}`;

const builderOptions = (batch: boolean) => ({
  mode: 'outbound' as const,
  batch,
});

// A captured batch may arrive as an array of records; the tree shows one record.
const recordsOf = (record: any): any[] =>
  Array.isArray(record) ? record : [record];

/** One-line description of what the endpoint would send; shared by the field strip and the editor. */
export function usePreviewSummary() {
  const { t } = useTranslation();
  return useCallback(
    (
      preview: OutboundPreview,
      hasOutputType = true,
    ): { text: string; tone: 'ok' | 'muted' | 'warn' | 'error' } => {
      if (!hasOutputType)
        return {
          text: t('Choose a Data Type to preview this filter'),
          tone: 'muted',
        };
      switch (preview.kind) {
        case 'empty':
          return { text: t('Enter a JQ filter'), tone: 'muted' };
        case 'custom':
          return {
            text: t('Preview not available for custom jq'),
            tone: 'muted',
          };
        case 'batch_mismatch':
          return {
            text: preview.writtenForBatch
              ? t(
                  'This filter was built for batch mode. Open the transformation editor to update it.',
                )
              : t(
                  'This filter was built without batch mode. Open the transformation editor to update it.',
                ),
            tone: 'warn',
          };
        case 'no_request':
          return {
            text: t(
              'No request (filter produced nothing / conditions not met)',
            ),
            tone: 'warn',
          };
        case 'error':
          return {
            text: t('Error: jq would fail on {{fields}}', {
              fields: preview.fields.join(', '),
            }),
            tone: 'error',
          };
        case 'request':
          return {
            text: `✓ ${preview.isArray ? t('1 request (array)') : t('1 request')} · ${preview.bytes} B`,
            tone: 'ok',
          };
      }
    },
    [t],
  );
}

export const SUMMARY_TONE_CLASSES: Record<string, string> = {
  ok: 'text-ui-cta-primary',
  muted: 'text-ui-text-secondary-text',
  warn: 'text-unlisted-orange',
  error: 'text-ui-status-error-destructive',
};

type OutboundTransformEditorProps = {
  annotation: JqTransformAnnotation;
  outputType: string;
  batch: boolean;
  value: string;
  onChange: (jq: string) => void;
  readOnly: boolean;
  samples: SamplesState;
  onRefreshSamples: () => void;
  sampleKey: string;
  onSampleKeyChange: (key: string) => void;
  codeMode?: boolean;
  onCodeModeChange?: (codeMode: boolean) => void;
};

export function OutboundTransformEditor({
  annotation,
  outputType,
  batch,
  value,
  onChange,
  readOnly,
  samples,
  onRefreshSamples,
  sampleKey,
  onSampleKeyChange,
  codeMode: initialCodeMode,
  onCodeModeChange,
}: OutboundTransformEditorProps) {
  const { t } = useTranslation();
  const summarize = usePreviewSummary();
  const dragPathRef = useRef<string[] | null>(null);
  const {
    sourceTreeRef,
    pickActive,
    sourceArmedPath,
    startKeyboardPick,
    activateLeaf,
    cancelKeyboardPick,
    cancelSourcePick,
  } = useSourcePick();
  const options = builderOptions(batch);

  // ── Builder state, seeded from the filter. A builder filter written for the other
  // batch setting is loaded and rewritten for this one (see the effect below).
  const [seed] = useState(() => {
    const parsed = parseJqToBuilderState(value, options);
    if (!parsed || parsed.isFullyParseable)
      return { parsed, convertedFrom: null as boolean | null };
    const other = parseJqToBuilderState(value, builderOptions(!batch));
    return other?.isFullyParseable
      ? { parsed: other, convertedFrom: !batch }
      : { parsed, convertedFrom: null };
  });
  const [conditionRows, setConditionRows] = useState<Row<Condition>[]>(() =>
    toRows(seed.parsed?.conds ?? []),
  );
  const [fieldRows, setFieldRows] = useState<Row<AdditionalField>[]>(() =>
    seed.parsed?.addl.length ? toRows(seed.parsed.addl) : emptyFieldRows(),
  );
  const [isCustom, setIsCustom] = useState(
    () =>
      !!seed.parsed &&
      !seed.parsed.isFullyParseable &&
      seed.convertedFrom === null,
  );
  const [isCodeMode, setIsCodeMode] = useState(
    () => initialCodeMode ?? isCustom,
  );
  const [codeEditorHeight, setCodeEditorHeight] = useState(
    CODE_EDITOR_MIN_HEIGHT,
  );
  const codeSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (seed.convertedFrom !== null && seed.parsed)
      onChange(
        genJq(seed.parsed.conds, {}, seed.parsed.addl, 'obv', {}, options),
      );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    },
    [],
  );

  const loadParsed = useCallback(
    (text: string) => {
      const parsed = parseJqToBuilderState(text, builderOptions(batch));
      if (!parsed) {
        setConditionRows([]);
        setFieldRows(emptyFieldRows());
        setIsCustom(false);
        return;
      }
      setIsCustom(!parsed.isFullyParseable);
      if (parsed.isFullyParseable) {
        setConditionRows(toRows(parsed.conds));
        setFieldRows(
          parsed.addl.length ? toRows(parsed.addl) : emptyFieldRows(),
        );
      }
    },
    [batch],
  );

  const commitBuilder = (
    nextConditions: Row<Condition>[],
    nextFields: Row<AdditionalField>[],
  ) => {
    setConditionRows(nextConditions);
    setFieldRows(nextFields);
    onChange(
      genJq(
        itemsOf(nextConditions),
        {},
        itemsOf(nextFields),
        'obv',
        {},
        options,
      ),
    );
  };
  const replaceAt = <T,>(rows: Row<T>[], index: number, item: T) =>
    rows.map((r, j) => (j === index ? { ...r, item } : r));

  const onCodeEdit = (text: string) => {
    onChange(text);
    if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    codeSyncTimer.current = setTimeout(() => loadParsed(text), 400);
  };

  const switchMode = (code: boolean) => {
    if (!code) {
      if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
      loadParsed(value);
    }
    setIsCodeMode(code);
    onCodeModeChange?.(code);
  };

  // ── Samples ──
  const example = exampleFor(annotation, outputType);
  const captured = useMemo(
    () => (samples.status === 'ready' ? samples.samples : []),
    [samples],
  );
  const sampleOptions = useMemo(
    () => [
      { value: EXAMPLE_KEY, label: t('Example (Gundi schema)') },
      ...captured.map((sample) => ({
        value: capturedSampleKey(sample),
        label: `${outputTypeLabel(annotation, outputType)} · ${isNaN(Date.parse(sample.captured_at)) ? sample.captured_at : new Date(sample.captured_at).toLocaleString()}`,
      })),
    ],
    [captured, annotation, outputType, t],
  );
  // A selection whose sample is gone after a Refresh falls back to the example.
  const selectedKey = sampleOptions.some((o) => o.value === sampleKey)
    ? sampleKey
    : EXAMPLE_KEY;
  // In batch mode a captured selection previews every captured record as the batch.
  const { treeRecord, input } = useMemo(() => {
    const capturedIndex = captured.findIndex(
      (sample) => capturedSampleKey(sample) === selectedKey,
    );
    const selectedRecords =
      capturedIndex >= 0
        ? recordsOf(captured[capturedIndex].record)
        : [example ?? {}];
    const batchRecords =
      capturedIndex >= 0
        ? captured.flatMap((s) => recordsOf(s.record))
        : selectedRecords;
    return {
      treeRecord: selectedRecords[0] ?? {},
      input: previewInput(batch ? batchRecords : selectedRecords, batch),
    };
  }, [selectedKey, captured, example, batch]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapsed = useCallback(
    (key: string) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      }),
    [],
  );
  const [sourceView, setSourceView] = useState<'record' | 'schema'>('record');
  const schema = inputSchemaFor(annotation, outputType, batch, (title) =>
    t('Batch of {{title}}', { title }),
  );

  const preview = useMemo(
    () => previewOutbound(value, input, batch),
    [value, input, batch],
  );
  const summary = summarize(preview);

  const samplesStatus = (() => {
    switch (samples.status) {
      case 'draft':
        return t('Save the connection first to capture data samples.');
      case 'loading':
        return t('Loading samples…');
      case 'error':
        switch (samples.reason) {
          case 'forbidden':
            return t('Only organization admins can read captured samples.');
          case 'not_signed_in':
            return t("Couldn't load samples: {{message}}", {
              message: t('Not signed in'),
            });
          case 'bad_response':
            return t("Couldn't load samples: {{message}}", {
              message: t('The connector returned no samples.'),
            });
          default:
            return t("Couldn't load samples: {{message}}", {
              message: samples.message || t('Request failed'),
            });
        }
      case 'ready':
        if (captured.length) return null;
        return samples.captureEnabled === false
          ? t('Turn on Capture data samples and wait for data to arrive.')
          : t(
              'No samples captured yet. Wait for data to arrive, then Refresh.',
            );
    }
  })();

  const namedFields = fieldRows.filter((r) => r.item.name);

  return (
    <div className="flex flex-col gap-3" data-testid="OutboundTransformEditor">
      <div className="grid grid-cols-2 gap-3">
        {/* ── Schema & example ── */}
        <section
          className={PANEL_CLASSES}
          data-testid="OutboundTransformEditor-schema-section"
          aria-label={t('Schema & example')}
        >
          <div className={HEADER_CLASSES}>
            <span>{t('Schema & example')}</span>
            <SegmentedToggle
              value={sourceView}
              options={[
                { value: 'record', label: t('Record') },
                { value: 'schema', label: t('Schema') },
              ]}
              onChange={(v) => setSourceView(v as 'record' | 'schema')}
            />
          </div>
          {sourceView === 'schema' ? (
            <div
              className="h-[392px]"
              data-testid="OutboundTransformEditor-schema"
            >
              {schema ? (
                <Editor
                  height="100%"
                  language="json"
                  theme="vs"
                  value={JSON.stringify(schema, null, 2)}
                  options={MONACO_OPTIONS(true)}
                />
              ) : (
                <div className="px-3.5 py-2 text-xs italic text-ui-text-primary-text">
                  {t('No schema for this data type.')}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col" style={{ height: 392 }}>
              <div className="flex items-center gap-2 border-b border-ui-divider-lines px-3.5 py-2">
                <SelectMenu
                  value={selectedKey}
                  options={sampleOptions}
                  onChange={onSampleKeyChange}
                  className="min-w-0 flex-1"
                  compact
                  ariaLabel={t('Sample record')}
                  dataTestId="OutboundTransformEditor-sample-picker"
                />
                <button
                  type="button"
                  className={buttonClasses.lightGreenSm}
                  onClick={onRefreshSamples}
                  disabled={
                    samples.status === 'draft' || samples.status === 'loading'
                  }
                >
                  {t('Refresh')}
                </button>
              </div>
              {samplesStatus && (
                <div
                  data-testid="OutboundTransformEditor-samples-status"
                  className="border-b border-ui-divider-lines bg-unlisted-very-light-gray px-3.5 py-1.5 text-[11px] text-ui-text-primary-text"
                >
                  {samplesStatus}
                </div>
              )}
              <div
                ref={sourceTreeRef}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return;
                  if (pickActive) {
                    e.preventDefault();
                    cancelKeyboardPick();
                  } else if (sourceArmedPath) {
                    e.preventDefault();
                    cancelSourcePick();
                  }
                }}
                className={`flex-1 overflow-y-auto py-1.5 ${pickActive ? 'rounded ring-2 ring-inset ring-ui-cta-primary' : ''}`}
              >
                {treeRecord &&
                typeof treeRecord === 'object' &&
                Object.keys(treeRecord).length ? (
                  Object.entries(treeRecord).map(([key, item]) => (
                    <TreeNode
                      key={key}
                      name={key}
                      value={item}
                      path={[key]}
                      depth={0}
                      collapsed={collapsed}
                      onToggle={toggleCollapsed}
                      readOnly={readOnly || isCustom}
                      dragPathRef={dragPathRef}
                      onLeafActivate={activateLeaf}
                      pickActive={pickActive}
                      armedPathKey={sourceArmedPath?.join('.')}
                    />
                  ))
                ) : (
                  <div className="px-3.5 py-2 text-xs italic text-ui-text-primary-text">
                    {t('No sample payload')}
                  </div>
                )}
              </div>
              <div className="border-t border-ui-divider-lines bg-unlisted-very-light-gray px-3.5 py-2 text-[11px] text-ui-text-primary-text">
                {pickActive
                  ? t(
                      'Click or press Enter on a field to select it — Esc to cancel',
                    )
                  : sourceArmedPath
                    ? t('Now click a mapping slot on the right — Esc to cancel')
                    : batch
                      ? t(
                          'Batch mode: the filter receives an array of these records.',
                        )
                      : t('Drag leaf fields onto mapping slots on the right')}
              </div>
            </div>
          )}
        </section>

        {/* ── Transformations ── */}
        <section
          className={PANEL_CLASSES}
          data-testid="OutboundTransformEditor-transformations-section"
          aria-label={t('Transformations')}
        >
          <div className={HEADER_CLASSES}>
            <span>{t('Transformations')}</span>
            <div className="flex items-center gap-2">
              {isCodeMode && (
                <button
                  type="button"
                  onClick={() => {
                    copyToClipboard(value, t);
                  }}
                  className="flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                  aria-label={t('Copy to clipboard')}
                >
                  <CopyToClipboard />
                </button>
              )}
              <SegmentedToggle
                value={isCodeMode ? 'code' : 'builder'}
                options={[
                  { value: 'builder', label: t('Builder') },
                  { value: 'code', label: t('Code') },
                ]}
                onChange={(v) => switchMode(v === 'code')}
              />
            </div>
          </div>
          {seed.convertedFrom !== null && (
            <div className="border-b border-ui-divider-lines bg-unlisted-light-blue px-3.5 py-2 text-xs text-unlisted-dark-blue-800">
              {batch
                ? t('The filter was rewritten for batch mode. Save to keep it.')
                : t(
                    'The filter was rewritten for single records. Save to keep it.',
                  )}
            </div>
          )}
          {isCodeMode ? (
            <div style={{ height: codeEditorHeight, overflow: 'hidden' }}>
              <Editor
                height="100%"
                language="jq"
                value={value}
                theme="vs"
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  lineHeight: 20,
                  padding: { top: 8 },
                  readOnly,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  automaticLayout: true,
                  renderLineHighlight: 'none',
                  fontFamily: MONO_FONT_FAMILY,
                }}
                beforeMount={registerJqLanguage}
                onMount={(e) => {
                  const fit = () =>
                    setCodeEditorHeight(
                      Math.max(CODE_EDITOR_MIN_HEIGHT, e.getContentHeight()),
                    );
                  fit();
                  e.onDidContentSizeChange(fit);
                }}
                onChange={(v) => onCodeEdit(v ?? '')}
              />
            </div>
          ) : isCustom ? (
            <div
              className="px-3.5 py-3"
              data-testid="OutboundTransformEditor-custom"
            >
              <div className="rounded-md bg-unlisted-very-light-orange px-3 py-2 text-xs text-unlisted-orange">
                {t(
                  "This filter uses custom jq the Builder can't show. Edit it in Code mode.",
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-3.5 py-3">
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className={SECTION_LABEL}>{t('Conditions')}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className={buttonClasses.lightGreenSm}
                      onClick={() =>
                        commitBuilder(
                          [
                            ...conditionRows,
                            ...toRows([
                              { field: '', op: 'not_null', value: '' },
                            ]),
                          ],
                          fieldRows,
                        )
                      }
                    >
                      {t('Add Condition')}
                    </button>
                  )}
                </div>
                {conditionRows.length === 0 && (
                  <div className="text-xs text-ui-text-primary-text">
                    {t('No conditions — all records pass through')}
                  </div>
                )}
                {conditionRows.map((row, index) => (
                  <ConditionRow
                    key={row.id}
                    condition={row.item}
                    index={index}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={(i, next) =>
                      commitBuilder(
                        replaceAt(conditionRows, i, next),
                        fieldRows,
                      )
                    }
                    onRemove={(i) =>
                      commitBuilder(
                        conditionRows.filter((_, j) => j !== i),
                        fieldRows,
                      )
                    }
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                  />
                ))}
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className={SECTION_LABEL}>{t('Body fields')}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className={buttonClasses.lightGreenSm}
                      onClick={() =>
                        commitBuilder(conditionRows, [
                          ...fieldRows,
                          ...emptyFieldRows(),
                        ])
                      }
                    >
                      {t('Add Field')}
                    </button>
                  )}
                </div>
                {namedFields.length === 0 && (
                  <div className="mb-2 text-xs text-ui-text-primary-text">
                    {t('No fields — the record is sent unchanged.')}
                  </div>
                )}
                {fieldRows.map((row, index) => (
                  <AdditionalRow
                    key={row.id}
                    field={row.item}
                    index={index}
                    sampleInput={treeRecord}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={(i, next) => {
                      const previous = fieldRows[i].item;
                      // Clearing a mapped path drops its transforms, as in the inbound builder.
                      const updated =
                        previous.path && !next.path
                          ? { ...next, transforms: undefined }
                          : next;
                      commitBuilder(
                        conditionRows,
                        replaceAt(
                          fieldRows,
                          i,
                          updated.transforms
                            ? {
                                ...updated,
                                transforms: normalizeTransforms(
                                  updated.transforms,
                                ),
                              }
                            : updated,
                        ),
                      );
                    }}
                    onRemove={(i) =>
                      commitBuilder(
                        conditionRows,
                        fieldRows.filter((_, j) => j !== i),
                      )
                    }
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Preview ── */}
      <section
        data-testid="OutboundTransformEditor-preview-section"
        aria-label={t('Preview')}
      >
        <OutputPreview
          title={t('Preview')}
          hasMappings={
            preview.kind === 'request' || preview.kind === 'no_request'
          }
          failedConditions={
            preview.kind === 'no_request' ? preview.failedConditions : []
          }
          output={preview.kind === 'request' ? preview.body : null}
          skippedMessage={t(
            'No request (filter produced nothing / conditions not met)',
          )}
          headerExtra={
            <span
              data-testid="OutboundTransformEditor-preview-summary"
              className={`text-xs font-normal ${SUMMARY_TONE_CLASSES[summary.tone]}`}
            >
              {summary.text}
            </span>
          }
          notice={
            preview.kind === 'request' ||
            preview.kind === 'no_request' ? undefined : (
              <div className="px-4 py-3 text-xs text-ui-text-primary-text">
                {preview.kind === 'custom'
                  ? t(
                      "Hand-written jq can't be previewed here; the portal has no jq engine. Check the result on the endpoint.",
                    )
                  : summary.text}
              </div>
            )
          }
        />
        <p className="mt-1.5 text-[11px] text-ui-text-secondary-text">
          {t(
            'Route field mappings run before this filter, so real records can differ from the samples.',
          )}
        </p>
      </section>
    </div>
  );
}

type OutboundTransformEditorModalProps = {
  sessions: EditorSessionStore;
  sessionKey: string;
  value: string;
  onSave: (jq: string) => void;
  onClose: () => void;
  readOnly: boolean;
  annotation: JqTransformAnnotation;
  outputType: string;
  batch: boolean;
  integrationId?: string;
  authToken?: string;
};

/** Draft-then-Save modal around the editor (EditorExpandModal pattern). The draft and UI
 *  choices are kept in the session store so a form remount doesn't lose them. */
export function OutboundTransformEditorModal({
  sessions,
  sessionKey,
  value,
  onSave,
  onClose,
  readOnly,
  annotation,
  outputType,
  batch,
  integrationId,
  authToken,
}: OutboundTransformEditorModalProps) {
  const { t } = useTranslation();
  const [session] = useState(() => {
    const existing = liveEditorSession(sessions, sessionKey, value);
    if (existing) return existing;
    const created: EditorSession = { base: value, draft: value };
    sessions.set(sessionKey, created);
    return created;
  });
  const updateSession = (patch: Partial<EditorSession>) => {
    const current = sessions.get(sessionKey);
    if (current) sessions.set(sessionKey, { ...current, ...patch });
  };
  const [draft, setDraft] = useState(session.draft);
  const [sampleKey, setSampleKey] = useState(session.sampleKey ?? EXAMPLE_KEY);
  const { state: samples, refresh } = useDataSamples({
    enabled: true,
    token: authToken,
    integrationId,
    action: samplesAction(annotation),
    outputType,
  });

  const changeDraft = (jq: string) => {
    setDraft(jq);
    updateSession({ draft: jq });
  };
  const close = () => {
    sessions.delete(sessionKey);
    onClose();
  };
  const save = () => {
    sessions.delete(sessionKey);
    onSave(draft);
    onClose();
  };

  return createPortal(
    <ModalShell
      title={t('Transformation editor')}
      subtitle={`${outputTypeLabel(annotation, outputType)}${batch ? ` · ${t('Batch Mode')}` : ''}`}
      onClose={close}
      maxWidthClass="max-w-6xl"
      footer={
        !readOnly ? (
          <>
            <button
              type="button"
              className={buttonClasses.primarySm}
              onClick={save}
            >
              {t('Save')}
            </button>
            <button
              type="button"
              className={buttonClasses.cancelSm}
              onClick={close}
            >
              {t('Cancel')}
            </button>
          </>
        ) : undefined
      }
    >
      <OutboundTransformEditor
        annotation={annotation}
        outputType={outputType}
        batch={batch}
        value={draft}
        onChange={changeDraft}
        readOnly={readOnly}
        samples={samples}
        onRefreshSamples={refresh}
        sampleKey={sampleKey}
        onSampleKeyChange={(key) => {
          setSampleKey(key);
          updateSession({ sampleKey: key });
        }}
        codeMode={session.codeMode}
        onCodeModeChange={(codeMode) => updateSession({ codeMode })}
      />
    </ModalShell>,
    document.body,
  );
}
